import { MeasureType, getMeasureTypeItems, normalizeMeasureType } from '../../../shared/utils/measure-type.utils';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormControl
} from '@angular/forms';
import {
  DataService,
  AuthService,
  ToasterService
} from 'src/app/shared/services';
import { ModelTaskModel } from 'src/app/shared/models';
import { BsModalRef } from 'ngx-bootstrap/modal';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { CoreImageUploadComponent } from 'src/app/shared/core-image-upload/core-image-upload.component';

@Component({
  selector: 'app-model-task-edit',
  templateUrl: './edit.component.html'
})
export class ModelTaskEditComponent implements OnInit {
  @ViewChild(CoreImageUploadComponent)
  coreImageUploadComp!: CoreImageUploadComponent;
  // Modal parameters
  items: any; // array of items to edit
  toolingReference: any[] = [];
  stateReference: any[] = [];
  tradeReference: any[] = [];
  activityReference: any[] = [];
  frequencyReference: any[] = [];
  criticalityReference: any[] = [];
  measureReference: { value: MeasureType; name: string }[] = [];
  models: any[] = [];

  // form instance
  form!: UntypedFormGroup;

  // Usage mode
  mode: 'mass' | 'single' | 'new' = 'single';

  // Target Id's
  targetIds: number[] = [];

  // UI Flag
  isLoading = false;
  data = null;
  isSubmitted = false;

  // PDF attachment state
  existingPdfName: string | null = null;
  pendingPdfFile: File | null = null;
  isDeletingPdf = false;
  isDownloadingPdf = false;

  readonly MAX_PDF_BYTES = 20 * 1024 * 1024;

  // Form reference Data
  parentReference: any;

  /**
   * @param formBuilder
   * @param service
   * @param auth
   * @param bsModalRef
   * @param translate
   */
  constructor(
    private formBuilder: UntypedFormBuilder,
    protected service: DataService,
    public auth: AuthService,
    public bsModalRef: BsModalRef,
    public translate: TranslateService,
    private toastr: ToasterService
  ) {
    this.isLoading = true;
  }

  /**
   *
   */
  async ngOnInit(): Promise<void> {
    // Sniff mode
    this.mode = this.sniffEditMode(this.items);

    // get target Ids
    this.targetIds = _.map(this.items, (el) => el.id);

    // Get default empty object
    if (['mass', 'new'].indexOf(this.mode) > -1) {
      this.items = [
        new ModelTaskModel({
          account: this.auth.getAccountId(),
          siteId: this.auth.getSiteId()
        })
      ];
    }

    // Push current Parent to init parentReference and have default selection
    if (['single'].indexOf(this.mode) > -1) {
      this.parentReference = _.map(this.items, (el) => {
        return {
          id: el.parentId,
          name: el.parent
        };
      }).filter((el) => el !== null);
    }

    this.measureReference = getMeasureTypeItems(this.translate);

    // Build form
    this.form = this.getForm(this.items[0]);

    // Load existing PDF attachment info
    if (this.mode === 'single') {
      this.existingPdfName = this.items[0]?.procedure_pdf_original_name ?? null;
    }

    // Loading done !
    this.isLoading = false;
  }

  /**
   * Sniff usage mode from items configuration
   */
  sniffEditMode(items: [ModelTaskModel?]): 'mass' | 'single' | 'new' {
    // Empty array => New Item
    if (items === undefined || items.length === 0) {
      return 'new';

      // More than one item => Mass Edit
    } else if (items.length > 1) {
      return 'mass';

      // Else => Single Edit
    } else {
      return 'single';
    }
  }

  /**
   *
   */
  getForm(item: ModelTaskModel): UntypedFormGroup {
    return this.formBuilder.group({
      id: new UntypedFormControl(item.id),
      component: new UntypedFormControl(item.component),
      description: new UntypedFormControl(item.description),
      keyword: new UntypedFormControl(item.keyword),
      duration: new UntypedFormControl(item.duration),
      criteria: new UntypedFormControl(item.criteria),
      measureType: new UntypedFormControl(normalizeMeasureType(item)),
      tools: new UntypedFormControl(item.toolIds),
      state: new UntypedFormControl(item.stateId),
      frequency: new UntypedFormControl(item.frequencyId),
      trade: new UntypedFormControl(item.tradeId),
      activity: new UntypedFormControl(item.activityId),
      criticalities: new UntypedFormControl(item.criticalityIds),
      procedure: new UntypedFormControl(item.procedure),
      procedure_text: new UntypedFormControl(item.procedure_text || ''),
      comment: new UntypedFormControl(item.comment),
      justification: new UntypedFormControl(item.justification)
    });
  }

  /**
   * Confirm callback with updated res
   */
  confirmFn = (res: any) => {};

  onPdfSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) return;
    if (file.size > this.MAX_PDF_BYTES) {
      this.toastr.showError('messages.pdf_too_large');
      input.value = '';
      return;
    }
    this.pendingPdfFile = file;
  }

  removePendingPdf(): void {
    this.pendingPdfFile = null;
  }

  downloadExistingPdf(): void {
    const taskId = this.items[0]?.id;
    if (!taskId || this.isDownloadingPdf) return;
    this.isDownloadingPdf = true;
    this.service
      .getBlob(`api/fastpm/modelTasks/${taskId}/procedure-pdf`)
      .subscribe({
        next: (res: any) => {
          this.isDownloadingPdf = false;
          this.service.createAndDownloadBlobFile(
            res.body,
            { type: 'application/pdf' },
            this.existingPdfName || 'procedure.pdf'
          );
        },
        error: () => {
          this.isDownloadingPdf = false;
          this.toastr.showError('messages.error');
        }
      });
  }

  deleteExistingPdf(): void {
    const taskId = this.items[0]?.id;
    if (!taskId) return;
    this.isDeletingPdf = true;
    this.service
      .delete('api/fastpm/modelTasks/_id_/procedure-pdf', taskId)
      .subscribe({
        next: () => {
          this.existingPdfName = null;
          this.isDeletingPdf = false;
        },
        error: () => {
          this.isDeletingPdf = false;
          this.toastr.showError('messages.error');
        }
      });
  }

  private uploadPendingPdf(taskId: number): void {
    if (!this.pendingPdfFile) {
      this.imagesUploaded({ status: 'success', data: null });
      return;
    }
    const formData = new FormData();
    formData.append('pdf', this.pendingPdfFile);
    this.service
      .postFiles(`api/fastpm/modelTasks/${taskId}/procedure-pdf`, formData)
      .subscribe({
        next: (res: any) => {
          this.existingPdfName = res?.procedure_pdf_original_name ?? null;
          this.pendingPdfFile = null;
          this.imagesUploaded({ status: 'success', data: res });
        },
        error: () => {
          this.toastr.showError('messages.error');
          this.isSubmitted = false;
        }
      });
  }

  /**
   *
   */
  save(): boolean | void {
    if (!this.form || (this.form && !this.form.valid)) {
      return false;
    }

    const procedureTitle = this.form.get('procedure')?.value;
    const procedureText = this.form.get('procedure_text')?.value;
    const hasProcedureContent =
      procedureText ||
      this.existingPdfName ||
      this.pendingPdfFile ||
      (this.mode === 'single' && this.coreImageUploadComp?.coreImageUploadControl?.value?.length);
    if (hasProcedureContent && !procedureTitle) {
      this.toastr.showError('messages.procedure_title_required');
      return false;
    }

    // Query pointer
    let query;

      if (this.mode === 'new') {
        const tmpData = _.pickBy(this.form.value, (el) => el !== null);
        const data: any[] = [];
        _.each(this.models, (modelId) => {
          const tmp = _.cloneDeep(tmpData);
          _.set(tmp, 'model', modelId);
          data.push(tmp);
        });
        query = this.service.saveAll('api/fastpm/modelTasks', data);
      } else if (this.mode === 'single') {
        const formValue = _.cloneDeep(this.form.value);
        query = this.service.update(
          'api/fastpm/modelTasks',
          this.targetIds[0],
          formValue
        );
      } else {
        const tmpData: any = {};
        _.each(this.form.controls, (control, name) => {
          if (control.dirty === true && control.value !== -1) {
            tmpData[name] = control.value;
          }
        });
        const data = _.map(this.targetIds, (id) => {
          const cloned = _.cloneDeep(tmpData);
          _.merge(cloned, { id });
          return cloned;
        });
        const retainFields = ['tools'];
        query = this.service.saveAllRetain(
          'api/fastpm/modelTasks',
          data,
          retainFields
        );
      }

      this.isSubmitted = true;
      query.subscribe(
        (res: any) => {
          if (this.mode === 'single') {
            this.coreImageUploadComp.uploadImages('fastpm_model_task', res.id);
          } else {
            this.imagesUploaded({ status: 'success', data: res });
          }
        },
        (err: any) => {
          console.log('err: ', err);
          this.isSubmitted = false;
        }
      );
  }

  imagesUploaded(event: any): void {
    if (event.status !== 'success') {
      this.toastr.showSuccess('messages.images_upload_error');
      this.isSubmitted = false;
      this.bsModalRef.hide();
      return;
    }

    // After images, upload pending PDF if any (single mode only)
    if (this.mode === 'single' && this.pendingPdfFile) {
      const taskId = this.items[0]?.id;
      this.uploadPendingPdf(taskId);
      return;
    }

    this.confirmFn(event);
    this.isSubmitted = false;
    this.bsModalRef.hide();
  }
}
